import {
  FEATURE_FLAGS,
  FEATURE_FLAGS_STORAGE_KEY,
  FEATURE_FLAG_QUERY_PARAM,
} from './constants';
import { FeatureFlagState, FeatureFlagUpdate } from './types';

/**
 * FeatureFlagManager handles feature flag state management,
 * localStorage persistence, and query parameter updates
 */
export class FeatureFlagManager {
  private static instance: FeatureFlagManager;
  private flags: FeatureFlagState = {};
  private listeners: Set<(flags: FeatureFlagState) => void> = new Set();

  private constructor() {
    this.initializeFlags();
    this.processQueryParameters();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): FeatureFlagManager {
    if (!FeatureFlagManager.instance) {
      FeatureFlagManager.instance = new FeatureFlagManager();
    }
    return FeatureFlagManager.instance;
  }

  /**
   * Initialize flags from localStorage or defaults
   */
  private initializeFlags(): void {
    try {
      const storedFlags = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
      const parsedFlags = storedFlags ? JSON.parse(storedFlags) : {};

      // Merge with defaults, ensuring all defined flags exist
      this.flags = Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
        acc[key] = parsedFlags[key] ?? FEATURE_FLAGS[key].defaultValue;
        return acc;
      }, {} as FeatureFlagState);

      this.saveToStorage();
    } catch (error) {
      console.warn('Failed to load feature flags from localStorage:', error);
      this.resetToDefaults();
    }
  }

  /**
   * Reset all flags to their default values
   */
  private resetToDefaults(): void {
    this.flags = Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
      acc[key] = FEATURE_FLAGS[key].defaultValue;
      return acc;
    }, {} as FeatureFlagState);
    this.saveToStorage();
  }

  /**
   * Process query parameters for feature flag updates
   */
  private processQueryParameters(): void {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const flagUpdates = urlParams.get(FEATURE_FLAG_QUERY_PARAM);

      if (flagUpdates) {
        this.parseAndApplyFlagUpdates(flagUpdates);
        // Remove the query parameter from URL without page reload
        this.cleanupUrl();
      }
    } catch (error) {
      console.warn('Failed to process feature flag query parameters:', error);
    }
  }

  /**
   * Parse and apply flag updates from query parameter
   * Format: ff=FLAG1:true,FLAG2:false,FLAG3:1
   */
  private parseAndApplyFlagUpdates(flagUpdates: string): void {
    const updates = flagUpdates.split(',');
    const appliedUpdates: FeatureFlagUpdate[] = [];

    updates.forEach((update) => {
      const [key, valueStr] = update.split(':');
      if (key && valueStr !== undefined && FEATURE_FLAGS[key]) {
        const value = this.parseBoolean(valueStr);
        this.setFlag(key, value);
        appliedUpdates.push({
          key,
          value,
          source: 'query',
          timestamp: Date.now(),
        });
      }
    });
  }

  /**
   * Parse boolean value from string
   */
  private parseBoolean(value: string): boolean {
    const lowerValue = value.toLowerCase();
    return lowerValue === 'true' || lowerValue === '1' || lowerValue === 'on';
  }

  /**
   * Remove feature flag query parameter from URL
   */
  private cleanupUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(FEATURE_FLAG_QUERY_PARAM);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Save flags to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(
        FEATURE_FLAGS_STORAGE_KEY,
        JSON.stringify(this.flags),
      );
    } catch (error) {
      console.warn('Failed to save feature flags to localStorage:', error);
    }
  }

  /**
   * Notify all listeners of flag changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener({ ...this.flags }));
  }

  /**
   * Get current flag value
   */
  public getFlag(key: string): boolean {
    return this.flags[key] ?? FEATURE_FLAGS[key]?.defaultValue ?? false;
  }

  /**
   * Check if flag is enabled
   */
  public isEnabled(key: string): boolean {
    return this.getFlag(key);
  }

  /**
   * Set flag value
   */
  public setFlag(key: string, value: boolean): void {
    if (!FEATURE_FLAGS[key]) {
      console.warn(`Unknown feature flag: ${key}`);
      return;
    }

    this.flags[key] = value;
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Reset flag to default value
   */
  public resetFlag(key: string): void {
    if (FEATURE_FLAGS[key]) {
      this.setFlag(key, FEATURE_FLAGS[key].defaultValue);
    }
  }

  /**
   * Reset all flags to default values
   */
  public resetAllFlags(): void {
    Object.keys(FEATURE_FLAGS).forEach((key) => {
      this.flags[key] = FEATURE_FLAGS[key].defaultValue;
    });
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Get all flags
   */
  public getAllFlags(): FeatureFlagState {
    return { ...this.flags };
  }

  /**
   * Subscribe to flag changes
   */
  public subscribe(listener: (flags: FeatureFlagState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Generate URL with feature flag query parameters
   */
  public generateFlagUrl(
    flagUpdates: Record<string, boolean>,
    baseUrl?: string,
  ): string {
    const url = new URL(baseUrl || window.location.href);
    const flagString = Object.entries(flagUpdates)
      .map(([key, value]) => `${key}:${value}`)
      .join(',');

    url.searchParams.set(FEATURE_FLAG_QUERY_PARAM, flagString);
    return url.toString();
  }
}
